import { FlexRow } from '@/components/common/Flex';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { BabyGoal } from '@common/goals';
import {
    isUsaRegionCode,
    UsaRegionCode,
    usaRegionNameByRegionCode,
} from '@common/location';
import { getGoalBaseConfig } from '@common/react/goals/baseConfigs';
import { BabyMedicalCostInfo } from '@common/types';
import { dt, isPastDate } from '@common/util/dateTime';
import { getRoundedUp } from '@common/util/math';
import pluralize from '@common/util/pluralize';
import { capitalize } from '@common/util/strings';
import { isBoolean, isEmpty, isNumber } from '@common/util/types';
import isEqual from 'lodash.isequal';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getIOrWe,
    getRecommendedRiskScoreUsingTargetDate,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

type InsuranceStatus = 'INSURED' | 'UNINSURED';
type Country = 'United States';

const medicalCostByUsaRegionCodeByInsuranceStatus: Record<
    InsuranceStatus,
    Record<UsaRegionCode, number>
> = {
    INSURED: {
        AL: 6_726_00, // $6,726
        AK: 14_157_00, // $14,157
        AZ: 9_367_00, // $9,367
        AR: 7_171_00, // $7,171
        CA: 9_573_00, // $9,573
        CO: 8_896_00, // $8,896
        CT: 9_504_00, // $9,504
        DE: 8_389_00, // $8,389
        DC: 5_645_00, // $5,645
        FL: 9_831_00, // $9,831
        GA: 9_214_00, // $9,214
        HI: 7_540_00, // $7,540
        ID: 8_018_00, // $8,018
        IL: 10_165_00, // $10,165
        IN: 8_778_00, // $8,778
        IA: 8_058_00, // $8,058
        KS: 8_328_00, // $8,328
        KY: 7_584_00, // $7,584
        LA: 7_126_00, // $7,126
        ME: 7_555_00, // $7,555
        MD: 8_041_00, // $8,041
        MA: 9_378_00, // $9,378
        MI: 8_121_00, // $8,121
        MN: 8_724_00, // $8,724
        MS: 8_121_00, // $8,121
        MO: 8_079_00, // $8,079
        MT: 7_224_00, // $7,224
        NE: 7_266_00, // $7,266
        NV: 9_307_00, // $9,307
        NH: 8_109_00, // $8,109
        NJ: 10_313_00, // $10,313
        NM: 7_522_00, // $7,522
        NY: 10_289_00, // $10,289
        NC: 8_427_00, // $8,427
        ND: 9_687_00, // $9,687
        OH: 7_620_00, // $7,620
        OK: 8_319_00, // $8,319
        OR: 9_162_00, // $9,162
        PA: 8_404_00, // $8,404
        RI: 6_828_00, // $6,828
        SC: 8_367_00, // $8,367
        SD: 8_502_00, // $8,502
        TN: 8_500_00, // $8,500
        TX: 9_484_00, // $9,484
        UT: 7_453_00, // $7,453
        VT: 7_977_00, // $7,977
        VA: 8_556_00, // $8,556
        WA: 8_884_00, // $8,884
        WV: 7_770_00, // $7,770
        WI: 12_206_00, // $12,206
        WY: 9_528_00, // $9,528
    },
    UNINSURED: {
        AL: 11_554_00, // $11,554
        AK: 24_430_00, // $24,430
        AZ: 16_418_00, // $16,418
        AR: 12_072_00, // $12,072
        CA: 16_580_00, // $16,580
        CO: 15_654_00, // $15,654
        CT: 17_482_00, // $17,482
        DE: 14_861_00, // $14,861
        DC: 14_208_00, // $14,208
        FL: 17_782_00, // $17,782
        GA: 16_370_00, // $16,370
        HI: 12_499_00, // $12,499
        ID: 13_173_00, // $13,173
        IL: 17_179_00, // $17,179
        IN: 14_262_00, // $14,262
        IA: 13_038_00, // $13,038
        KS: 13_567_00, // $13,567
        KY: 13_197_00, // $13,197
        LA: 12_374_00, // $12,374
        ME: 13_477_00, // $13,477
        MD: 14_512_00, // $14,512
        MA: 17_215_00, // $17,215
        MI: 13_159_00, // $13,159
        MN: 13_684_00, // $13,684
        MS: 13_159_00, // $13,159
        MO: 13_193_00, // $13,193
        MT: 11_795_00, // $11,795
        NE: 11_400_00, // $11,400
        NV: 16_015_00, // $16,015
        NH: 14_765_00, // $14,765
        NJ: 19_400_00, // $19,400
        NM: 12_876_00, // $12,876
        NY: 19_059_00, // $19,059
        NC: 14_416_00, // $14,416
        ND: 15_462_00, // $15,462
        OH: 12_421_00, // $12,421
        OK: 14_694_00, // $14,694
        OR: 15_285_00, // $15,285
        PA: 15_489_00, // $15,489
        RI: 12_238_00, // $12,238
        SC: 14_492_00, // $14,492
        SD: 16_026_00, // $16,026
        TN: 14_873_00, // $14,873
        TX: 17_097_00, // $17,097
        UT: 12_227_00, // $12,227
        VT: 14_784_00, // $14,784
        VA: 15_303_00, // $15,303
        WA: 14_946_00, // $14,946
        WV: 13_332_00, // $13,332
        WI: 20_818_00, // $20,818
        WY: 15_810_00, // $15,810
    },
};

const medicalCostByCountryByInsuranceStatus: Record<
    InsuranceStatus,
    Record<Country, number>
> = {
    INSURED: {
        'United States': 8_750_00, // $8,750
    },
    UNINSURED: {
        'United States': 15_000_00, // $15,000
    },
};

const paidLeaveInfoByUsaRegionCode: Record<
    UsaRegionCode,
    { weeks: number; fractionOfPay: number; note?: string } | null
> = {
    AL: null,
    AK: null,
    AZ: null,
    AR: null,
    CA: {
        weeks: 8,
        fractionOfPay: 0.55,
        note: 'Up to maximum of $1,067 weekly.',
    },
    CO: { weeks: 12, fractionOfPay: 1 },
    CT: { weeks: 12, fractionOfPay: 1 },
    DE: { weeks: 12, fractionOfPay: 1 },
    DC: { weeks: 8, fractionOfPay: 1 },
    FL: null,
    GA: null,
    HI: { weeks: 4, fractionOfPay: 0 },
    ID: null,
    IL: null,
    IN: null,
    IA: null,
    KS: null,
    KY: null,
    LA: null,
    ME: { weeks: 10, fractionOfPay: 0 },
    MD: null,
    MA: { weeks: 12, fractionOfPay: 1 },
    MI: null,
    MN: { weeks: 12, fractionOfPay: 0 },
    MS: null,
    MO: null,
    MT: null,
    NE: null,
    NV: null,
    NH: { weeks: 6, fractionOfPay: 0.6 },
    NJ: { weeks: 12, fractionOfPay: 0 },
    NM: null,
    NY: { weeks: 12, fractionOfPay: 1 },
    NC: null,
    ND: null,
    OH: null,
    OK: null,
    OR: { weeks: 12, fractionOfPay: 1 },
    PA: null,
    RI: { weeks: 5, fractionOfPay: 1 },
    SC: null,
    SD: null,
    TN: null,
    TX: null,
    UT: null,
    VT: { weeks: 12, fractionOfPay: 0 },
    VA: null,
    WA: { weeks: 12, fractionOfPay: 1 },
    WV: null,
    WI: { weeks: 6, fractionOfPay: 1 },
    WY: null,
};

export const BABY: GoalConfig<BabyGoal> = {
    inputSteps: [
        {
            id: 'isInsured',
            title: 'Will you have health insurance?',
            fieldConfigs: [
                {
                    fieldName: 'isInsured',
                    fieldType: 'radioCards',
                    fieldOptions: [
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ],
                    bottomContent: ({ goalData }) => {
                        const { projectedMedicalCostInfo, isInsured } =
                            goalData;
                        if (!projectedMedicalCostInfo || !isBoolean(isInsured))
                            return <FlexRow mt={8} h={8} />;
                        return (
                            <Paragraph
                                fontSize="sm"
                                color="textMuted"
                                mt={8}
                                h={8}
                            >
                                {`Medical expenses for an ${
                                    isInsured ? 'insured' : 'uninsured'
                                } pregnancy and childbirth average ${toDollars(
                                    projectedMedicalCostInfo.amount
                                )} in ${
                                    projectedMedicalCostInfo.locationName
                                }. Courtesy of PolicyScout.`}
                            </Paragraph>
                        );
                    },
                },
            ],
        },
        {
            id: 'isSavingForLostIncome',
            title: 'Do you also want to save for income given up during parental leave?',
            fieldConfigs: [
                {
                    fieldName: 'isSavingForLostIncome',
                    fieldType: 'radioCards',
                    fieldOptions: [
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ],
                    bottomContent: ({ userInfo }) => {
                        if (!isUsaRegionCode(userInfo.region)) return <></>;

                        const usaRegionName =
                            usaRegionNameByRegionCode[userInfo.region];
                        const paidLeaveInfo =
                            paidLeaveInfoByUsaRegionCode[userInfo.region];

                        const text = paidLeaveInfo
                            ? `${usaRegionName} provides ${pluralize(
                                  'week',
                                  paidLeaveInfo.weeks,
                                  true
                              )} of ${
                                  Math.round(
                                      paidLeaveInfo.fractionOfPay * 10000
                                  ) / 100
                              }% paid parental leave. ${
                                  paidLeaveInfo.note ?? ''
                              }`
                            : `${usaRegionName} does not provide parental leave support.`;

                        return (
                            <Paragraph fontSize="sm" color="textMuted" mt={8}>
                                {text}
                            </Paragraph>
                        );
                    },
                },
            ],
            onNext: ({ goalData, goForward }) => {
                if (goalData.isSavingForLostIncome === false) {
                    goForward('yearMonth');
                } else {
                    goForward();
                }
            },
        },
        {
            id: 'weeksOfIncome',
            title: 'How many additional weeks of income do you want to save?',
            fieldConfigs: [
                {
                    fieldName: 'weeksOfIncome',
                    fieldType: 'integer',
                    fieldValueDefault: 7,
                    min: 1,
                    slider: {
                        min: 1,
                        max: 52,
                        step: 1,
                    },
                    bottomContent: ({ goalData, userInfo }) => {
                        const { weeksOfIncome } = goalData;
                        if (!isNumber(weeksOfIncome)) return <></>;
                        return (
                            <>
                                {/* HACK: This will be replaced as part of PLE-327 */}
                                <Text
                                    fontSize="xl"
                                    ml={weeksOfIncome < 10 ? 5 : 8}
                                    mt={'-40px'}
                                >
                                    {pluralize('week', weeksOfIncome, false)}
                                </Text>
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={8}
                                    h={18}
                                >
                                    {userInfo.annualIncome &&
                                    goalData.projectedLostIncomeAmount
                                        ? `Based on your income of ${toDollars(
                                              userInfo.annualIncome
                                          )}, you’d be planning to save an additional ${toDollars(
                                              goalData.projectedLostIncomeAmount
                                          )} to make up for parental leave.`
                                        : ``}
                                </Paragraph>
                            </>
                        );
                    },
                },
            ],
        },
        {
            id: 'yearMonth',
            title: 'Roughly, when do you think you want to have your child?',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            onBack: ({ goalData, goBack }) => {
                if (goalData.isSavingForLostIncome === false) {
                    goBack('isSavingForLostIncome');
                } else {
                    goBack();
                }
            },
            isNextButtonDisabled: (goalData) =>
                !goalData.targetDate || isPastDate(goalData.targetDate),
        },
        {
            id: 'targetAmount',
            title: 'Choose a goal target for having a baby that you feel comfortable with.',
            fieldConfigs: [
                {
                    fieldName: 'targetAmount',
                    fieldType: 'currency',
                    min: 0,
                    slider: ({ goalData }) => {
                        const projectedTargetAmount = getProjectedTargetAmount(
                            goalData.projectedMedicalCostInfo?.amount,
                            goalData.projectedLostIncomeAmount
                        );
                        return projectedTargetAmount
                            ? {
                                  min: 0,
                                  max: projectedTargetAmount * 2,
                                  step: 1_000_00, // $1,000
                              }
                            : {
                                  min: 0,
                                  max: 100_000_00, // $100,000
                                  step: 1_000_00, // $1,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) => {
                        const {
                            isSavingForLostIncome,
                            weeksOfIncome,
                            projectedMedicalCostInfo,
                            projectedLostIncomeAmount,
                        } = goalData;
                        const projectedTargetAmount = getProjectedTargetAmount(
                            projectedMedicalCostInfo?.amount,
                            projectedLostIncomeAmount
                        );
                        if (
                            !projectedMedicalCostInfo ||
                            !isNumber(projectedTargetAmount)
                        ) {
                            return undefined;
                        }

                        if (isSavingForLostIncome && isNumber(weeksOfIncome)) {
                            return (
                                <>
                                    <Paragraph
                                        fontSize="sm"
                                        color="textMuted"
                                        mt={8}
                                    >
                                        {`With an estimated medical expense of ${toDollars(
                                            projectedMedicalCostInfo.amount
                                        )} and ${
                                            weeksOfIncome > 0
                                                ? weeksOfIncome
                                                : 'no'
                                        } additional ${pluralize(
                                            'week',
                                            weeksOfIncome,
                                            false
                                        )} of income put aside, that totals ${toDollars(
                                            projectedTargetAmount
                                        )}.`}
                                    </Paragraph>
                                    <LinkText
                                        fontSize="sm"
                                        fontWeight="medium"
                                        mt={3}
                                        onClick={() => {
                                            showSlideup(
                                                'targetAmountAssumptions'
                                            );
                                        }}
                                    >
                                        See assumptions
                                    </LinkText>
                                </>
                            );
                        }

                        if (!isSavingForLostIncome) {
                            return (
                                <Paragraph
                                    fontSize="sm"
                                    color="textMuted"
                                    mt={8}
                                >{`Medical expenses are estimated to be roughly ${toDollars(
                                    projectedTargetAmount
                                )}.`}</Paragraph>
                            );
                        }
                    },
                },
            ],
        },
    ],
    assumptions: [
        {
            id: 'targetAmountAssumptions',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Your estimated medical expenses are calculated using:
                    </Paragraph>
                    <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                        <ListItem>
                            State-averages for prenatal expenses including:
                            doctor appointments, ultrasounds, and testing (hCG,
                            Chorionic Villus, and amniocentesis tests)
                        </ListItem>
                        <ListItem>
                            State-averages for natural or cesarean births
                            expenses including: anaesthetics, delivery room
                            usage fees, testing, IVs, and more.
                        </ListItem>
                    </UnorderedList>
                    <Paragraph fontSize="sm" lineHeight="tall" mt={4}>
                        Each health insurance and hospital bills differently. If
                        you’re planning actively, contact your health insurance
                        and hospital to understand what charges to expect.
                    </Paragraph>
                    <Paragraph fontSize="sm" lineHeight="tall" mt={4}>
                        We used the 2022 OECD estimate of the median income tax
                        rate of 28% in our estimates. (Source: Organization for
                        Economic Co-operation and Development)
                    </Paragraph>
                </>
            ),
        },
    ],
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (isEmpty(goalData.targetAmount) || isEmpty(goalData.targetDate)) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const weeksStr = goalData.weeksOfIncome
            ? pluralize('week', goalData.weeksOfIncome, true)
            : undefined;

        const iOrWe = getIOrWe(goalData);
        const color = BABY.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() => onGoalStatementClick('targetAmount')}
                    color={color}
                />
                {' for baby costs '}
                {weeksStr && (
                    <>
                        {'and '}
                        <GoalStatementLinkText
                            text={weeksStr}
                            onClick={() =>
                                onGoalStatementClick('weeksOfIncome')
                            }
                            color={color}
                        />
                        {' of leave '}
                    </>
                )}
                {'by '}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {'.'}
            </>
        );
    },
    getAssumptionsContent: () => {
        return (
            <>
                <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                    {'Your estimated medical expenses are calculated using:'}
                </Paragraph>
                <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                    <ListItem>{`State-averages for prenatal expenses including: doctor appointments, ultrasounds, and testing (hCG, Chorionic Villus, and amniocentesis tests)`}</ListItem>
                    <ListItem>{`State-averages for natural or cesarean births expenses including: anaesthetics, delivery room usage fees, testing, IVs, and more.`}</ListItem>
                </UnorderedList>
                <Paragraph mt={4} fontSize="sm" lineHeight="tall">
                    {`Each health insurance and hospital bills differently. If you’re planning actively, contact your health insurance and hospital to understand what charges to expect.`}
                </Paragraph>
                <Paragraph mt={4} fontSize="sm" lineHeight="tall">
                    We used the 2022 OECD estimate of the median income tax rate
                    of 28% in our estimates. (Source: Organization for Economic
                    Co-operation and Development)
                </Paragraph>
            </>
        );
    },
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
        userInfo,
    }): Promise<GoalData<BabyGoal>> => {
        // Clear weeksOfIncome if not saving for lost income
        const weeksOfIncome = newGoalData.isSavingForLostIncome
            ? newGoalData.weeksOfIncome
            : undefined;

        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedMedicalCostInfo = getProjectedMedicalCostInfo({
            isInsured: newGoalData.isInsured,
            regionCode: userInfo.region,
        });

        const projectedLostIncomeAmount = getProjectedLostIncomeAmount({
            isSavingForLostIncome: newGoalData.isSavingForLostIncome,
            annualIncome: userInfo.annualIncome,
            weeksOfIncome,
        });

        const projectedTargetAmount = getProjectedTargetAmount(
            projectedMedicalCostInfo?.amount,
            projectedLostIncomeAmount
        );

        const oldProjectedAmount = getProjectedTargetAmount(
            oldGoalData.projectedMedicalCostInfo?.amount,
            oldGoalData.projectedLostIncomeAmount
        );

        return {
            ...newGoalData,
            weeksOfIncome,
            targetDate,
            projectedMedicalCostInfo,
            projectedLostIncomeAmount,

            // update targetAmount if projected amount changed using formula above
            ...(!isEqual(oldProjectedAmount, projectedTargetAmount) && {
                targetAmount: projectedTargetAmount,
            }),
        };
    },
    getRecommendedRiskScore: (config) =>
        getRecommendedRiskScoreUsingTargetDate(config),
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('BABY'),
};

const getProjectedMedicalCostInfo = ({
    isInsured,
    regionCode,
}: {
    isInsured?: boolean;
    regionCode?: UsaRegionCode | string | null;
}): BabyMedicalCostInfo | undefined => {
    if (isEmpty(isInsured)) return undefined;

    if (isUsaRegionCode(regionCode)) {
        const amountDecimal =
            medicalCostByUsaRegionCodeByInsuranceStatus[
                isInsured ? 'INSURED' : 'UNINSURED'
            ][regionCode];
        return {
            locationName: usaRegionNameByRegionCode[regionCode],
            amount: getRoundedUp(amountDecimal, 100_00), // $100
        };
    } else {
        const amountDecimal =
            medicalCostByCountryByInsuranceStatus[
                isInsured ? 'INSURED' : 'UNINSURED'
            ]['United States'];
        return {
            locationName: 'United States',
            amount: getRoundedUp(amountDecimal, 250_00), // $250
        };
    }
};

const getProjectedLostIncomeAmount = ({
    isSavingForLostIncome,
    annualIncome,
    weeksOfIncome,
}: {
    isSavingForLostIncome?: boolean;
    annualIncome?: number | null;
    weeksOfIncome?: number | null;
}): number | undefined => {
    if (isSavingForLostIncome === false) return 0;

    if (!isNumber(annualIncome) || !isNumber(weeksOfIncome)) return undefined;

    // N.B. Use a 2022 OECD estimate of the median income tax rate of 28%
    const amountDecimal = (annualIncome / 52) * weeksOfIncome * 0.72;

    return getRoundedUp(amountDecimal, 100_00); // $100
};

/**
 * Returns the sum of the amounts ONLY IF both amounts are defined.
 */
const getProjectedTargetAmount = (
    projectedMedicalCostAmount?: number,
    projectedLostIncomeAmount?: number
): number | undefined =>
    isNumber(projectedMedicalCostAmount) && isNumber(projectedLostIncomeAmount)
        ? projectedMedicalCostAmount + projectedLostIncomeAmount
        : undefined;
